<div class="modal-content">
  <div class="modal-header align-items-center">
    <div class="d-flex flex-column">
      <h2 class="modal-title mb-1">{{ 'companyProfile.createInvoiceModal.title' | translate }}</h2>
      <p class="text-muted mb-0">{{ 'companyProfile.createInvoiceModal.subtitle' | translate }}</p>
    </div>
    <i class="fa-solid fa-xmark fs-2 cursor-pointer" (click)="close()"></i>
  </div>

  <div class="modal-body">
    <form [formGroup]="form">
      <!-- User Selection -->
      <div class="section mb-4">
        <h5 class="section-title mb-3">
          <i class="fa fa-user me-2 text-primary"></i>
          {{ 'companyProfile.createInvoiceModal.recipient' | translate }}
        </h5>

        @if (selectedUser) {
          <div class="selected-user-card">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ selectedUser.full_name }}</strong>
                <br />
                <small class="text-muted">{{ selectedUser.email }}</small>
              </div>
              <button type="button" class="btn btn-sm btn-light" (click)="clearUser()">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        } @else {
          <div class="mb-3">
            <label class="form-label">{{ 'companyProfile.createInvoiceModal.searchUser' | translate }}</label>
            <input
              type="text"
              class="form-control"
              [placeholder]="'companyProfile.createInvoiceModal.searchPlaceholder' | translate"
              (input)="onUserSearch($event)"
              [disabled]="useEmailOnly"
            />
            @if (searching) {
              <div class="search-loading">
                <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                <span class="ms-2">{{ 'common.searching' | translate }}</span>
              </div>
            }
            @if (searchResults.length > 0 && !useEmailOnly) {
              <div class="search-results">
                @for (user of searchResults; track user.user_id) {
                  <div class="search-result-item" (click)="selectUser(user)">
                    <strong>{{ user.full_name }}</strong>
                    <br />
                    <small class="text-muted">{{ user.email }}</small>
                  </div>
                }
              </div>
            }
          </div>

          <div class="form-check mb-3">
            <input
              class="form-check-input"
              type="checkbox"
              [checked]="useEmailOnly"
              (change)="toggleEmailOnly()"
              id="useEmailOnly"
            />
            <label class="form-check-label" for="useEmailOnly">
              {{ 'companyProfile.createInvoiceModal.enterEmailManually' | translate }}
            </label>
          </div>

          @if (useEmailOnly) {
            <div>
              <label class="form-label required">{{ 'companyProfile.createInvoiceModal.userEmail' | translate }}</label>
              <input
                type="email"
                class="form-control"
                formControlName="user_email"
                [placeholder]="'companyProfile.createInvoiceModal.emailPlaceholder' | translate"
              />
            </div>
          }
        }
      </div>

      <!-- Payment Type -->
      <div class="section mb-4">
        <h5 class="section-title mb-3">
          <i class="fa fa-credit-card me-2 text-primary"></i>
          {{ 'companyProfile.createInvoiceModal.paymentType' | translate }}
        </h5>
        <div class="row g-3">
          <div class="col-md-6">
            <div
              class="payment-type-card"
              [class.selected]="form.get('payment_type')?.value === 'one_time'"
              (click)="form.patchValue({payment_type: 'one_time'})"
            >
              <i class="fa fa-receipt"></i>
              <h6>{{ 'companyProfile.createInvoiceModal.oneTimePayment' | translate }}</h6>
              <small class="text-muted">{{ 'companyProfile.createInvoiceModal.singleCharge' | translate }}</small>
            </div>
          </div>
          <div class="col-md-6">
            <div
              class="payment-type-card"
              [class.selected]="form.get('payment_type')?.value === 'subscription'"
              (click)="form.patchValue({payment_type: 'subscription'})"
            >
              <i class="fa fa-sync-alt"></i>
              <h6>{{ 'companyProfile.createInvoiceModal.monthlySubscription' | translate }}</h6>
              <small class="text-muted">{{ 'companyProfile.createInvoiceModal.recurringCharge' | translate }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Due Date (for one-time only) -->
      @if (form.get('payment_type')?.value === 'one_time') {
        <div class="mb-4">
          <label class="form-label">{{ 'companyProfile.createInvoiceModal.dueDateOptional' | translate }}</label>
          <input
            type="date"
            class="form-control"
            formControlName="due_date"
          />
        </div>
      }

      <!-- Line Items -->
      <div class="section mb-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="section-title mb-0">
            <i class="fa fa-list me-2 text-primary"></i>
            {{ 'companyProfile.createInvoiceModal.lineItems' | translate }}
          </h5>
          <button type="button" class="btn btn-sm btn-light-primary" (click)="addLineItem()">
            <i class="fa fa-plus me-1"></i> {{ 'companyProfile.createInvoiceModal.addItem' | translate }}
          </button>
        </div>

        @if (loadingServices) {
          <div class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
          </div>
        } @else if (lineItems.length === 0) {
          <div class="empty-items text-center py-4">
            <i class="fa fa-inbox text-muted fs-1 mb-2"></i>
            <p class="text-muted mb-0">{{ 'companyProfile.createInvoiceModal.noItemsHint' | translate }}</p>
          </div>
        } @else {
          <div class="line-items-list" formArrayName="line_items">
            @for (item of lineItems.controls; track $index; let i = $index) {
              <div class="line-item-card" [formGroupName]="i">
                <div class="row g-2 align-items-end">
                  <div class="col-md-2">
                    <label class="form-label">{{ 'companyProfile.createInvoiceModal.type' | translate }}</label>
                    <select
                      class="form-select form-select-sm"
                      formControlName="type"
                      (change)="onTypeChange(i, $any($event.target).value)"
                    >
                      <option value="service">{{ 'companyProfile.createInvoiceModal.fromServices' | translate }}</option>
                      <option value="custom">{{ 'companyProfile.createInvoiceModal.custom' | translate }}</option>
                    </select>
                  </div>

                  @if (item.get('type')?.value === 'service') {
                    <div class="col-md-6">
                      <label class="form-label">{{ 'companyProfile.createInvoiceModal.service' | translate }}</label>
                      <select
                        class="form-select form-select-sm"
                        formControlName="service_id"
                        (change)="onServiceChange(i, $any($event.target).value)"
                      >
                        <option [ngValue]="null">{{ 'companyProfile.createInvoiceModal.selectService' | translate }}</option>
                        @for (service of services; track service.service_id) {
                          <option [value]="service.service_id">
                            {{ service.service_name }} ({{ formatPrice(service.price_cents) }} {{ service.currency }})
                          </option>
                        }
                      </select>
                    </div>
                  } @else {
                    <div class="col-md-4">
                      <label class="form-label">{{ 'companyProfile.createInvoiceModal.description' | translate }}</label>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        formControlName="description"
                        [placeholder]="'companyProfile.createInvoiceModal.descriptionPlaceholder' | translate"
                      />
                    </div>
                    <div class="col-md-2">
                      <label class="form-label">{{ 'companyProfile.createInvoiceModal.price' | translate }}</label>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        [value]="item.get('unit_price_cents')?.value / 100"
                        (input)="item.patchValue({unit_price_cents: $any($event.target).value * 100})"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  }

                  <div class="col-md-1">
                    <label class="form-label">{{ 'companyProfile.createInvoiceModal.qty' | translate }}</label>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      formControlName="quantity"
                      min="1"
                    />
                  </div>

                  <div class="col-md-2 text-end">
                    <label class="form-label">{{ 'companyProfile.createInvoiceModal.total' | translate }}</label>
                    <div class="line-total">{{ formatPrice(getLineItemTotal(i)) }} NOK</div>
                  </div>

                  <div class="col-md-1 text-end">
                    <label class="form-label d-none d-md-block">&nbsp;</label>
                    <button type="button" class="btn btn-sm btn-light-danger" (click)="removeLineItem(i)">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Total -->
      @if (lineItems.length > 0) {
        <div class="total-section">
          <div class="d-flex justify-content-between align-items-center">
            <span class="total-label">{{ 'companyProfile.createInvoiceModal.totalAmount' | translate }}</span>
            <span class="total-value">{{ formatPrice(getTotalAmount()) }} NOK</span>
          </div>
        </div>
      }
    </form>
  </div>

  <div class="modal-footer">
    <div class="row w-100">
      <div class="col-12 d-flex justify-content-end gap-2">
        <app-button
          [variant]="'neutral'"
          [label]="'common.cancel' | translate"
          (clicked)="close()"
        />
        <app-button
          [variant]="'primary'"
          [label]="'companyProfile.createInvoiceModal.createInvoice' | translate"
          [iconClass]="'fa fa-paper-plane'"
          [loading]="saving"
          [disabled]="!canSubmit()"
          (clicked)="save()"
        />
      </div>
    </div>
  </div>
</div>
